/**
 * Export rules for cropped image uploads (FNF-1239, decided on FNF-1238): one
 * master per image, up to 1600px on the long edge, WebP 0.85, size-checked
 * after compression. Screens get smaller sizes from the image CDN
 * (?width=), so the master is what a full-width image on a 3x phone needs.
 * Redrawing on a canvas also drops EXIF (location, camera data), so nothing
 * extra is needed for that.
 */

/** Long edge of the stored master. Callers pass less for small images (logo, profile). */
export const MASTER_LONG_EDGE = 1600;
export const EXPORT_QUALITY = 0.85;
/** Large phone photos are fine: they are shrunk before upload. Same limit as common-service. */
export const MAX_SOURCE_BYTES = 10 * 1024 * 1024;
/** Limit on what is actually uploaded, after crop and compression. */
export const MAX_UPLOAD_BYTES = 1 * 1024 * 1024;

export type ExportMimeType = 'image/webp' | 'image/jpeg' | 'image/png';

let webpEncodeSupported: boolean | undefined;

/** Safari can't encode WebP: its canvas silently returns PNG instead. */
export function supportsWebpEncode(): boolean {
  if (webpEncodeSupported === undefined) {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    webpEncodeSupported = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  }
  return webpEncodeSupported;
}

/** WebP where the browser can encode it; otherwise PNG to keep transparency, else JPEG. */
export function exportMimeType(
  allowTransparency: boolean,
  webp: boolean = supportsWebpEncode(),
): ExportMimeType {
  if (webp) return 'image/webp';
  return allowTransparency ? 'image/png' : 'image/jpeg';
}

export function extensionFor(mimeType: string): string {
  if (mimeType === 'image/webp') return 'webp';
  if (mimeType === 'image/jpeg') return 'jpg';
  return 'png';
}

/**
 * Scale from the display size to the master: as large as the crop's own
 * pixels allow, up to a long edge of maxLongEdge, never below 1x (layouts
 * expect at least the display size).
 */
export function exportScale(
  cropSourceWidth: number,
  targetWidth: number,
  targetHeight: number,
  maxLongEdge: number = MASTER_LONG_EDGE,
): number {
  const toMaster = maxLongEdge / Math.max(targetWidth, targetHeight);
  return Math.max(1, Math.min(toMaster, cropSourceWidth / targetWidth));
}

/** Error for a picked file before cropping, or null when it can go to the crop dialog. */
export function sourceImageError(file: File): string | null {
  if (!file.type.startsWith('image/')) return 'Please choose an image file.';
  if (file.size > MAX_SOURCE_BYTES) return 'Image size exceeds 10MB!';
  return null;
}

/** Error for the cropped, compressed result, or null when it can be uploaded. */
export function uploadSizeError(blob: Blob): string | null {
  return blob.size > MAX_UPLOAD_BYTES
    ? 'Image is still over 1MB after compression. Please choose another photo.'
    : null;
}

/** Name the file after its real type, so common-service stores the right extension. */
export function croppedImageFile(blob: Blob, baseName: string): File {
  const type = blob.type || 'image/png';
  return new File([blob], `${baseName}.${extensionFor(type)}`, { type });
}

/**
 * Draw the crop at `width` x `height`. Large reductions are done in halving
 * steps: one big drawImage looks jagged in Firefox, which ignores
 * imageSmoothingQuality. `opaque` fills white first, so transparent areas
 * don't turn black in a JPEG.
 */
export function renderCrop(
  image: CanvasImageSource,
  sx: number,
  sy: number,
  sw: number,
  sh: number,
  width: number,
  height: number,
  opaque: boolean,
): HTMLCanvasElement {
  let source: CanvasImageSource = image;
  let srcX = sx;
  let srcY = sy;
  let srcW = sw;
  let srcH = sh;

  while (srcW >= width * 2 && srcH >= height * 2) {
    const step = document.createElement('canvas');
    step.width = Math.round(srcW / 2);
    step.height = Math.round(srcH / 2);
    const stepCtx = step.getContext('2d');
    if (!stepCtx) break;
    stepCtx.imageSmoothingEnabled = true;
    stepCtx.imageSmoothingQuality = 'high';
    stepCtx.drawImage(source, srcX, srcY, srcW, srcH, 0, 0, step.width, step.height);
    source = step;
    srcX = 0;
    srcY = 0;
    srcW = step.width;
    srcH = step.height;
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  if (opaque) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
  }
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, srcX, srcY, srcW, srcH, 0, 0, width, height);
  return canvas;
}
